import type { Widen } from '../catalogue';
import type { svAuth } from '../sv/auth';

export const enAuth: Widen<typeof svAuth> = {
  login: {
    title: 'Login | EVID',
    email: 'Email',
    emailIncorrect: 'Email is incorrect',
    password: 'Password',
    passwordIncorrect: 'Password is incorrect',
    submit: 'Login',
    forgotPassword: 'Forgot password'
  },
  forgotPassword: {
    title: 'Forgot Password | EVID',
    sent: 'Password reset email sent.',
    submit: 'Send password reset email',
    backToLogin: 'Back to sign in'
  },
  signup: {
    invalidLink: 'Signup link is invalid!',
    signingUpWith: 'Signing up with <b>{{email}}</b>',
    displayName: 'Display Name',
    displayNameMissing: 'Enter a display name',
    failed: 'Registration failed. The link may have expired — ask for a new invite.',
    submit: 'Register'
  },
  newPassword: {
    invalidLink: 'Reset link is invalid!',
    enterFor: 'Enter new password for <b>{{email}}</b>',
    password: 'New Password',
    failed: 'Could not set the password. The link may have expired — ask for a new one.',
    submit: 'Submit'
  },
  camera: {
    photo: 'Photo',
    video: 'Video',
    loading: 'Loading camera',
    loadingSlow: '(If this is taking a long time, try refreshing the page)',
    grantCamera: 'You must grant camera permission!',
    info: {
      title: 'Info & Help',
      enableVideoTitle: 'Enable Video Functionality',
      enableVideoText:
        'You have disabled the video functionality. If you want to enable it, please click on the button below. Before enabling, make sure that no other application is using your microphone.',
      microphoneTitle: 'Problem with Microphone',
      microphoneText:
        'Is the application not working? It could be because your microphone is not available or is currently being used by another application.',
      disableVideoText:
        'To make the application work, you can disable the video functionality by clicking the button below.',
      enableVideo: 'Enable video',
      disableVideo: 'Disable video',
      permissionsTitle: 'Granting Permissions',
      permissionsText:
        "If the application is still not working, we need access to your camera, microphone, and location. Enabling video functionality will provide a better user experience. Please click 'Allow' when prompted by your browser to grant us permission.",
      privacy:
        'We value your privacy and will only use this information for the intended purpose of our application.<br/><policy>Read our privacy policy here.</policy>',
      guide:
        'If the application is still not working or you have accidentally denied access or permissions; <guide>Read our guide</guide>'
    },
    takePhoto: 'Take photo',
    startRecording: 'Start recording',
    stopRecording: 'Stop recording'
  },
  cameraPage: {
    invalidLinkTitle: 'Invalid link',
    invalidLink: {
      default: 'This link is invalid or has expired. Please ask for a new link',
      expired: 'This link has expired. Please ask for a new link',
      invalid: 'This link is invalid. Please ask for a new link'
    },
    grantCamera: 'You must grant camera permission in the browsers setting!',
    captureFailed: 'Something went wrong! please try again',
    uploadFailed: 'Upload failed!',
    uploadFailedWithError: 'Upload failed! {{error}}',
    noPosition: 'No position',
    searchingPosition: 'Searching for position…',
    uploadImage: 'Upload image',
    uploadVideo: 'Upload video',
    imageAlt: 'image_to_upload',
    messagePlaceholder: 'Enter a message to attach to the upload',
    send: 'Send',
    retake: 'Retake',
    receiptTitle: 'Thank you!',
    receiptPhotoTo: 'The photo has been sent to {{name}}.',
    receiptPhoto: 'The photo has been sent.',
    receiptVideoTo: 'The video has been sent to {{name}}.',
    receiptVideo: 'The video has been sent.',
    takeAnother: 'Take another'
  },
  locationHelp: {
    deniedTitle: 'Location is off for {{browser}}',
    onlyWithLocation: 'Photos can only be sent with a location.',
    inApp: 'You opened the link inside {{browser}}. Open it in Safari instead:',
    workPhone: 'On a work phone the setting may be locked. Open the link in Safari instead.',
    retry: 'Try again',
    copyLink: 'Copy the link',
    openInSafari: 'Open in Safari',
    copied: 'The link is copied.',
    copiedPasteInSafari: 'The link is copied. Paste it into Safari.',
    searching: 'Searching for your position…',
    slow: 'This is taking longer than usual. Try going outdoors or closer to a window.',
    continueWithout: 'Continue without location',
    confirmWithout:
      'The photo is saved without a position. The recipient sees that the location is missing. Choose this only if location cannot be turned on.',
    steps: {
      safariSettings:
        'Open <strong>Settings</strong> → <strong>Privacy & Security</strong> → <strong>Location Services</strong>. Turn on <strong>Location Services</strong> and choose <strong>Safari Websites</strong> → <strong>While Using the App</strong>.',
      safariSite:
        'Come back here, tap <strong>aA</strong> in the address bar → <strong>Website Settings</strong> → <strong>Location</strong> → <strong>Allow</strong>.',
      tapRetry: 'Tap <strong>Try again</strong>.',
      inAppOpen: 'Tap <strong>Open in Safari</strong>, or <strong>Copy the link</strong> and paste it into Safari.',
      inAppAllow: 'Allow location when Safari asks.',
      iosSettings:
        'Open <strong>Settings</strong> → <strong>Privacy & Security</strong> → <strong>Location Services</strong>. Turn on <strong>Location Services</strong> and choose <strong>{{browser}}</strong> → <strong>While Using the App</strong>.',
      iosRetry:
        'Come back here, tap <strong>Try again</strong> and answer <strong>Allow</strong> when {{browser}} asks.',
      androidLocation: 'Swipe down from the top of the screen and turn on <strong>Location</strong>.',
      androidSite:
        'Tap the icon to the left of the address → <strong>Permissions</strong> → <strong>Location</strong> → <strong>Allow</strong>.',
      otherSite:
        'Allow location for this page in the browser settings, often through the icon to the left of the address.'
    },
    browser: {
      googleApp: 'the Google app',
      theApp: 'the app',
      theBrowser: 'the browser'
    }
  },
  download: {
    loading: 'Loading...',
    errorAlt: 'error',
    pdfReport: 'Download pdf report',
    file: 'Download file',
    downloadAs: 'Download as...',
    png: 'PNG',
    jpeg: 'JPEG',
    pdf: 'PDF',
    video: 'Download video',
    history: 'History',
    unknownUserHidden: 'a team member (name hidden)',
    unknownUserRemoved: 'a removed user',
    assigneeChanged: 'Assignee changed to <b>{{name}}</b>',
    assigneeChangedUnknown: 'Assignee changed to <i>{{name}}</i>',
    assigneeRemoved: 'Assignee changed to <i>Nobody</i>',
    statusChanged: 'Status changed to <b>{{status}}</b>',
    markedOpen: 'Marked open',
    markedClosed: 'Marked closed',
    wrote: '<b>{{name}}</b> wrote at {{date}} {{time}}',
    wroteUnknown: '<i>{{name}}</i> wrote at {{date}} {{time}}'
  }
};
